// src/components/auth/LoginScreen.js
//
// Pass hosting/UX — the production sign-in card. Preserves the existing Firebase auth exactly (Google
// popup + email/password), the ?redirect= handling, and the canonical dashboard/workspace resolver. Adds a
// real password-reset flow, human-friendly errors, loading states, and duplicate-submit protection. No
// Facebook in the primary UI; no signup fields here (Create account → /signup). Presentation lives in
// AuthShell; this is the card only.

import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase';
import { PRODUCT_NAME } from '../../config/product';
import { AUTH_T as T } from './AuthShell';

const friendly = (code = '') => ({
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': 'Incorrect email or password.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/missing-password': 'Please enter your password.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Google sign-in was cancelled.',
  'auth/network-request-failed': 'Network problem. Check your connection and try again.',
}[code]);

const input = {
  width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: 14.5, fontFamily: T.f,
  border: `1px solid ${T.borderStrong}`, borderRadius: 10, background: '#fff', color: T.ink, outline: 'none',
};
const label = { display: 'block', fontSize: 12.5, fontWeight: 600, color: T.ink2, margin: '0 0 6px' };
const primaryBtn = (disabled) => ({
  width: '100%', padding: '12px 14px', fontSize: 15, fontWeight: 700, fontFamily: T.f, color: '#fff',
  background: disabled ? '#9DB4F0' : T.blue, border: 'none', borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
});

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><g>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </g></svg>
  );
}

export default function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get('email') || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState('');       // '' | 'email' | 'google' | 'reset'
  const [error, setError] = useState('');
  // §7 — deep-link into reset mode via ?reset=1 (the invite "Reset password" action).
  const [mode, setMode] = useState(() => (new URLSearchParams(location.search).get('reset') === '1' ? 'reset' : 'signin'));
  const [resetSent, setResetSent] = useState(false);

  const goNext = () => navigate(new URLSearchParams(location.search).get('redirect') || '/dashboard');

  // Create-account must carry the invite context forward: preserve ?redirect= (e.g. /invite/<code>) and the
  // invited ?email= so a first-time invited user creates the right account and returns to the invite.
  const signupPath = (() => {
    const src = new URLSearchParams(location.search);
    const p = new URLSearchParams();
    const redirect = src.get('redirect'); if (redirect) p.set('redirect', redirect);
    const invitedEmail = src.get('email'); if (invitedEmail) p.set('email', invitedEmail);
    const qs = p.toString();
    return qs ? `/signup?${qs}` : '/signup';
  })();

  const onGoogle = async () => {
    if (busy) return; setError(''); setBusy('google');
    try { await signInWithPopup(auth, new GoogleAuthProvider()); goNext(); }
    catch (e) { setError(friendly(e?.code) || "We couldn't sign you in with Google. Please try again."); }
    finally { setBusy(''); }
  };

  const onEmail = async (e) => {
    e.preventDefault(); if (busy) return; setError(''); setBusy('email');
    try { await signInWithEmailAndPassword(auth, email.trim(), password); goNext(); }
    catch (err) { setError(friendly(err?.code) || 'Something went wrong. Please try again.'); }
    finally { setBusy(''); }
  };

  // Pass 88.3 §3 — BRANDED (Voletus) reset via the server callable (Resend), NOT the Firebase client default
  // email. The callable is enumeration-safe and always resolves success, so the UI shows the same neutral
  // "if an account exists…" confirmation for every address.
  const onReset = async (e) => {
    e.preventDefault(); if (busy) return; setError(''); setBusy('reset');
    try { await httpsCallable(functions, 'requestPasswordReset')({ email: email.trim() }); setResetSent(true); }
    catch (err) { setResetSent(true); } // never reveal failures/existence; neutral confirmation
    finally { setBusy(''); }
  };

  if (mode === 'reset') {
    return (
      <div>
        <h2 style={{ fontFamily: T.fD, fontSize: 26, fontWeight: 800, margin: '0 0 6px' }}>Reset your password</h2>
        <p style={{ fontSize: 14, color: T.ink2, margin: '0 0 22px' }}>Enter your email and we'll send you a reset link.</p>
        {resetSent ? (
          <div data-testid="reset-sent" style={{ fontSize: 14, color: '#065F46', background: '#E1F5EE', border: '1px solid #A7E3CE', borderRadius: 10, padding: '12px 14px' }}>
            If an account exists for <strong>{email}</strong>, a reset link is on its way. Check your inbox.
          </div>
        ) : (
          <form onSubmit={onReset} noValidate>
            <label htmlFor="reset-email" style={label}>Email</label>
            <input id="reset-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={input} />
            {error && <div data-testid="auth-error" role="alert" style={{ color: '#B91C1C', fontSize: 13, margin: '10px 0 0' }}>{error}</div>}
            <button type="submit" data-testid="reset-submit" disabled={!!busy} style={{ ...primaryBtn(!!busy), marginTop: 16 }}>{busy === 'reset' ? 'Sending reset link…' : 'Send reset link'}</button>
          </form>
        )}
        <button type="button" onClick={() => { setMode('signin'); setResetSent(false); setError(''); }}
          style={{ marginTop: 16, background: 'none', border: 'none', color: T.blue, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>← Back to sign in</button>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontFamily: T.fD, fontSize: 28, fontWeight: 800, margin: '0 0 6px' }}>Welcome back</h2>
      <p style={{ fontSize: 14.5, color: T.ink2, margin: '0 0 24px' }}>Sign in to continue to {PRODUCT_NAME}.</p>

      <button type="button" data-testid="google-signin" onClick={onGoogle} disabled={!!busy}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px 14px',
          fontSize: 14.5, fontWeight: 600, fontFamily: T.f, color: T.ink, background: '#fff', border: `1px solid ${T.borderStrong}`, borderRadius: 10, cursor: busy ? 'default' : 'pointer' }}>
        <GoogleIcon /> {busy === 'google' ? 'Opening Google…' : 'Continue with Google'}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        <span style={{ fontSize: 12, color: T.ink3 }}>or continue with email</span>
        <div style={{ flex: 1, height: 1, background: T.border }} />
      </div>

      <form onSubmit={onEmail} noValidate>
        <label htmlFor="login-email" style={label}>Email</label>
        <input id="login-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ ...input, marginBottom: 14 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <label htmlFor="login-password" style={label}>Password</label>
          <button type="button" data-testid="forgot-password" onClick={() => { setMode('reset'); setError(''); }}
            style={{ background: 'none', border: 'none', color: T.blue, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Forgot password?</button>
        </div>
        <input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required style={input} />
        {error && <div data-testid="auth-error" role="alert" style={{ color: '#B91C1C', fontSize: 13, margin: '12px 0 0' }}>{error}</div>}
        <button type="submit" data-testid="email-signin" disabled={!!busy} style={{ ...primaryBtn(!!busy), marginTop: 18 }}>{busy === 'email' ? 'Signing in…' : 'Sign in'}</button>
      </form>

      <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${T.border}`, fontSize: 13.5, color: T.ink2, lineHeight: 1.6 }}>
        <div><strong style={{ color: T.ink }}>Have an invitation?</strong> Open your invitation link to activate your account.</div>
        <div style={{ marginTop: 6 }}>New to {PRODUCT_NAME}? <Link to={signupPath} style={{ color: T.blue, fontWeight: 600, textDecoration: 'none' }}>Create account</Link></div>
      </div>
    </div>
  );
}
